import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBriefcase, 
  faUsers, 
  faGraduationCap, 
  faHandshake,
  faHeart,
  faMapMarkerAlt,
  faIdCard,
  faGlassCheers,
  faCar,
  faPercent
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { theme } from '../../../styles/theme';

const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

const CareersContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 800px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 4rem 0;
`;

const BenefitCard = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: center;
`;

const IconWrapper = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme.colors.accent};
  border-radius: 50%;
  
  svg {
    color: white;
    font-size: 32px;
  }
`;

const BenefitTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const BenefitText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
`;

const CurrentOpenings = styled.div`
  margin: 4rem 0;
`;

const OpeningCard = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const OpeningTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const OpeningDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const OpeningMeta = styled.p`
  font-size: 1rem;
  line-height: 1.5;
  color: ${props => props.theme.colors.primary};
  opacity: 0.9;
  margin-bottom: 1rem;
`;

const ListHeading = styled.h4`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.15rem;
  color: ${props => props.theme.colors.primary};
  margin: 1.25rem 0 0.5rem;
`;

const BulletList = styled.ul`
  font-size: 1rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
  padding-left: 1.5rem;
`;

const VolunteerBenefits = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1.5rem;
  margin: 0 0 1.5rem;
  padding: 1.5rem;
  background: ${props => props.theme.colors.offWhite};
  border-radius: 10px;
`;

const VolunteerBenefitItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.6rem;
`;

const VolunteerBenefitIcon = styled.div`
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const VolunteerBenefitText = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.4;
`;

const ApplyButton = styled.button`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  cursor: pointer;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
  }
`;

const ApplyLink = styled(Link)`
  display: inline-block;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.theme.colors.primary};

  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.primary};
  }
`;

const NewBadge = styled.span`
  display: inline-block;
  background: ${props => props.theme.colors.accent};
  color: white;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  padding: 0.25rem 0.7rem;
  border-radius: 12px;
  margin-left: 0.75rem;
  vertical-align: middle;
  position: relative;
  top: -2px;
`;

const ViewFullSpec = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: ${props => props.theme.colors.accent};
  padding: 1rem 2rem;
  border-radius: 25px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.theme.colors.accent};
  margin-left: 1rem;

  &:hover {
    background-color: ${props => props.theme.colors.accent};
    color: white;
  }

  @media (max-width: 600px) {
    margin-left: 0;
    margin-top: 0.75rem;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

const ContactSection = styled.div`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 4rem 2rem;
  text-align: center;
  margin-top: 4rem;
`;

const ContactTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  margin-bottom: 1.5rem;
`;

const ContactText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto 2rem;
`;

const Careers = () => {
  return (
    <CareersContainer>
      <ParallaxBanner 
        title="Careers at Belvoir Castle"
        subtitle="Join Our Historic Team"
        image={DAY_TICKETS_BANNER_CDN}
      />
      
      <ContentContainer>
        <SectionTitle>Build Your Career in a Historic Setting</SectionTitle>
        <IntroText>
          At Belvoir Castle, we're not just preserving history - we're creating it every day. 
          Join our dedicated team and become part of a unique heritage that spans centuries. 
          Whether you're passionate about hospitality, conservation, or customer service, 
          we offer rewarding opportunities in a truly special environment.
        </IntroText>
        
        <BenefitsGrid>
          <BenefitCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faHeart} />
            </IconWrapper>
            <BenefitTitle>Passionate Team</BenefitTitle>
            <BenefitText>
              Work alongside dedicated professionals who share your passion for history, 
              hospitality, and excellence.
            </BenefitText>
          </BenefitCard>
          
          <BenefitCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faGraduationCap} />
            </IconWrapper>
            <BenefitTitle>Growth Opportunities</BenefitTitle>
            <BenefitText>
              We invest in our team's development through training programs and 
              career advancement opportunities.
            </BenefitText>
          </BenefitCard>
          
          <BenefitCard>
            <IconWrapper>
              <FontAwesomeIcon icon={faHandshake} />
            </IconWrapper>
            <BenefitTitle>Work-Life Balance</BenefitTitle>
            <BenefitText>
              Enjoy flexible working arrangements and a supportive environment that 
              values your well-being.
            </BenefitText>
          </BenefitCard>
        </BenefitsGrid>
        
        <CurrentOpenings>
          <SectionTitle>Current Opportunities</SectionTitle>

          {/* ── Social Media Manager ── */}
          <OpeningCard>
            <OpeningTitle>
              Social Media Manager — Full-Time
              <NewBadge>Now Hiring</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Belvoir Castle, Leicestershire (In person) &nbsp;·&nbsp; Full-Time &nbsp;·&nbsp; Social media &amp; content focus
            </OpeningMeta>
            <OpeningDescription>
              Belvoir Castle is seeking a highly motivated Social Media Manager to support the estate's marketing
              strategy. Suited to recent graduates in Digital Marketing, Marketing, Communications, Media or a
              related discipline, the role focuses primarily on social media management and content creation,
              alongside wider marketing and administrative support.
            </OpeningDescription>
            <OpeningDescription>
              We're looking for someone with strong communication skills, creativity, and an organised, proactive
              approach — ready to begin or develop their career in a prestigious heritage environment.
            </OpeningDescription>
            <ListHeading>Key Focus Areas</ListHeading>
            <BulletList>
              <li>Planning, creating and scheduling content across Instagram, Facebook, TikTok and X</li>
              <li>Photography, video capture and written content that reflects the Belvoir brand</li>
              <li>Community engagement, performance tracking and continuous improvement</li>
              <li>Email campaigns, admin support, asset libraries and event marketing</li>
            </BulletList>
            <ListHeading>Essential Skills</ListHeading>
            <BulletList>
              <li>Degree in Digital Marketing, Marketing, Communications, Media, or equivalent experience</li>
              <li>Strong knowledge of major social platforms and excellent written communication</li>
              <li>Confidence in photography and video capture, with strong organisational skills</li>
              <li>Proficiency in Microsoft Office; ability to work independently and as part of a team</li>
            </BulletList>
            <ButtonRow>
              <ApplyLink to="/about/careers/social-media-manager">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/social-media-manager">
                View Full Spec →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>

          {/* ── Retail Sales Assistant ── */}
          <OpeningCard>
            <OpeningTitle>
              Retail Sales Assistant — Part-Time / Flexible
              <NewBadge>Now Hiring</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Belvoir Castle Estate, Leicestershire &nbsp;·&nbsp; Part-Time, Flexible &nbsp;·&nbsp; Weekends essential
            </OpeningMeta>
            <OpeningDescription>
              We are looking for friendly and enthusiastic Retail Assistants to join the retail team at Belvoir Castle.
              This is a flexible, part-time opportunity supporting our growing retail offer across the estate. The role
              will involve working across a range of Belvoir retail spaces including our Castle Gift Shops, Food to go
              areas, Engine Yard stores and Pet Shop.
            </OpeningDescription>
            <OpeningDescription>
              This is a great opportunity for someone looking to build experience in retail and customer service within
              a busy visitor destination. Due to the rural location of Belvoir Castle Estate, applicants will need
              reliable transport to travel to site.
            </OpeningDescription>
            <ListHeading>What You'll Be Doing</ListHeading>
            <BulletList>
              <li>Welcoming visitors and providing excellent customer service</li>
              <li>Assisting customers with purchases and operating the till</li>
              <li>Supporting visual merchandising and maintaining beautiful shop displays</li>
              <li>Replenishing stock and helping keep stores well presented</li>
              <li>Working across multiple retail locations on the estate as required</li>
            </BulletList>
            <ListHeading>What We're Looking For</ListHeading>
            <BulletList>
              <li>A friendly, positive and proactive attitude</li>
              <li>Confidence engaging with visitors and customers</li>
              <li>Flexibility to work across different retail spaces</li>
              <li>Availability to work weekends (Saturday and Sunday) is essential</li>
              <li>Retail, Customer Service or Café experience is helpful but not essential</li>
            </BulletList>
            <ButtonRow>
              <ApplyLink to="/about/careers/retail-sales-assistant">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/retail-sales-assistant">
                View Full Spec →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>

          {/* ── Tarot Reader — Gypsy Caravan ── */}
          <OpeningCard>
            <OpeningTitle>
              Tarot Reader — Gypsy Caravan
              <NewBadge>Now Hiring</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Gypsy Caravan, Belvoir Estate, Leicestershire &nbsp;·&nbsp; Seasonal Licence &nbsp;·&nbsp; Self-Employed &nbsp;·&nbsp; June, July &amp; August 2026
            </OpeningMeta>
            <OpeningDescription>
              We're inviting applications from experienced, professional tarot readers to operate from our
              characterful Gypsy Caravan for the summer season. Set within the beautiful grounds of Belvoir
              Estate, this is a unique opportunity to trade from an atmospheric and distinctive space,
              attracting thousands of estate visitors throughout the summer.
            </OpeningDescription>
            <OpeningDescription>
              This is a commercial licence arrangement — you operate as a self-employed practitioner, setting
              your own reading prices and retaining your income. Licence fee terms will be discussed at interview.
            </OpeningDescription>
            <ListHeading>What We're Looking For</ListHeading>
            <BulletList>
              <li>Proven experience as a professional tarot reader or similar practitioner</li>
              <li>Warm, engaging manner with a wide range of visitors</li>
              <li>Available across June, July &amp; August — trading days aligned with estate open days</li>
              <li>Public liability insurance (or willing to obtain prior to start)</li>
            </BulletList>
            <ListHeading>Desirable</ListHeading>
            <BulletList>
              <li>Experience at heritage venues, events, or country estates</li>
              <li>Complementary skills (astrology, palmistry, crystal work etc.)</li>
              <li>An existing social media presence or following</li>
            </BulletList>
            <ButtonRow>
              <ApplyLink to="/about/careers/tarot-reader-gypsy-caravan">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/tarot-reader-gypsy-caravan">
                View Full Details →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>

          {/* ── Rural Estate Assistant ── */}
          <OpeningCard>
            <OpeningTitle>
              Rural Estate Assistant — Land &amp; Property
              <NewBadge>Now Hiring</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Belvoir Estate, Leicestershire (On-site) &nbsp;·&nbsp; Full Time, Permanent &nbsp;·&nbsp; 40 hrs/week &nbsp;·&nbsp; £26,000 – £30,000 per annum
            </OpeningMeta>
            <OpeningDescription>
              A newly created post arising from a strategic restructure of the Land &amp; Property department.
              Belvoir Estate is a large, mixed-use rural estate comprising approximately 500 tenancies and a
              multi-million pound rent roll, spanning residential, commercial, agricultural, renewables,
              hospitality, leisure, minerals, and events.
            </OpeningDescription>
            <OpeningDescription>
              The Rural Estate Assistant will sit at the heart of day-to-day operations, providing broad
              administrative and operational support to the Property Manager and Assistant Land &amp; Property
              Manager. An excellent opportunity for a motivated individual looking to build a career in rural
              estate or property management.
            </OpeningDescription>
            <ListHeading>Key Responsibilities</ListHeading>
            <BulletList>
              <li>Day-to-day administrative support to the Property Manager and team</li>
              <li>Compliance &amp; performance reporting across the let estate</li>
              <li>First-stage debt management correspondence and arrears tracking</li>
              <li>Cover for Property Administrator and Maintenance &amp; Compliance Coordinator</li>
              <li>Tenant enquiries, accompanied viewings, and end-of-tenancy administration</li>
            </BulletList>
            <ListHeading>Essential Skills</ListHeading>
            <BulletList>
              <li>Strong administrative and organisational skills with high attention to detail</li>
              <li>Excellent written and verbal communication skills</li>
              <li>Proficient in Microsoft Office and comfortable with digital systems</li>
              <li>Discretion in handling confidential tenancy and financial information</li>
            </BulletList>
            <ButtonRow>
              <ApplyLink to="/about/careers/rural-estate-assistant">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/rural-estate-assistant">
                View Full Spec →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>

          {/* ── Digital Marketing Graduate Role ── */}
          <OpeningCard>
            <OpeningTitle>
              Digital Marketing Assistant — Graduate Role
              <NewBadge>Now Hiring</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Belvoir Castle, Leicestershire (On-site) &nbsp;·&nbsp; Graduate role &nbsp;·&nbsp; Open to recent graduates &nbsp;·&nbsp; 24–40 hrs/week (flexible) &nbsp;·&nbsp; Competitive salary
            </OpeningMeta>
            <OpeningDescription>
              This is a genuine paid graduate role — not a coffee-making internship. From day one you will be a real
              member of the marketing team, with real responsibility, working across all of Belvoir Castle's
              commercial channels. Beyond marketing, this role opens a door into our full digital stack: React,
              React Native, Node.js, WordPress, and AI-powered tooling.
            </OpeningDescription>
            <OpeningDescription>
              Open to recent graduates ready to start their career in marketing and digital.
              If you're qualified and keen to learn on a real team with real responsibility, we'd love to hear from you.
            </OpeningDescription>
            <ListHeading>You Will Work Across</ListHeading>
            <BulletList>
              <li>Social media content creation and paid campaigns (Meta, Google)</li>
              <li>Email marketing and database growth</li>
              <li>Brand & reputation management</li>
              <li>Tech & AI — React, WordPress CMS, Node.js, automation platforms</li>
            </BulletList>
            <ListHeading>What We're Looking For</ListHeading>
            <BulletList>
              <li>Recent graduates in Marketing, Digital Media, Computer Science, or equivalent experience</li>
              <li>Curious, driven, technically minded — with a creative eye for content and storytelling</li>
              <li>Any coding experience is a strong plus (HTML, CSS, JavaScript, PHP, Python)</li>
              <li>Full driving licence required</li>
            </BulletList>
            <ButtonRow>
              <ApplyLink to="/about/careers/digital-marketing-graduate">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/digital-marketing-graduate">
                View Full Spec →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>

          {/* ── Software & Digital Development Graduate Role ── */}
          <OpeningCard>
            <OpeningTitle>
              Software & Digital Development Assistant — Graduate Role
              <NewBadge>Now Hiring</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Belvoir Castle, Leicestershire (On-site) &nbsp;·&nbsp; Graduate role &nbsp;·&nbsp; Open to recent graduates &nbsp;·&nbsp; 24–40 hrs/week (flexible) &nbsp;·&nbsp; Competitive salary
            </OpeningMeta>
            <OpeningDescription>
              A genuine paid graduate role for someone ambitious, technically curious, and keen to understand
              what sits behind the technology — not just how digital platforms look, but how they work, connect,
              perform, and improve customer experience.
            </OpeningDescription>
            <OpeningDescription>
              The role spans WordPress, React, Node.js, AI tools, analytics, automation, and booking flows —
              with direct mentoring and exposure to Belvoir Castle's full technology stack from day one.
            </OpeningDescription>
            <ListHeading>Suited To</ListHeading>
            <BulletList>
              <li>Recent graduates in Computer Science, Software Development, Digital Media, or Marketing Technology</li>
              <li>Self-taught candidates with strong digital or technical experience and demonstrable projects</li>
              <li>Anyone excited to help a historic estate become a leader in digital innovation</li>
            </BulletList>
            <ListHeading>You Will Work Across</ListHeading>
            <BulletList>
              <li>WordPress CMS — custom plugins, REST API integrations, theme development</li>
              <li>React frontend — UI components, booking flows, customer-facing features</li>
              <li>Node.js & PHP backend — APIs, integrations, automation</li>
              <li>AI tools, analytics, and digital marketing systems</li>
            </BulletList>
            <ButtonRow>
              <ApplyLink to="/about/careers/software-digital-graduate">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/software-digital-graduate">
                View Full Spec →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>

          {/* ── Volunteer Gardener ── */}
          <OpeningCard>
            <OpeningTitle>
              Volunteer Gardener
              <NewBadge>Volunteer</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Belvoir Castle Gardens, Leicestershire &nbsp;·&nbsp; Volunteer &nbsp;·&nbsp; Flexible days
            </OpeningMeta>
            <OpeningDescription>
              We're looking for hands-on volunteers to help care for the beautiful gardens at Belvoir Castle.
              As a Volunteer Gardener, you'll work alongside our gardens team on practical horticultural tasks —
              keeping the borders, lawns and formal gardens looking their best.
            </OpeningDescription>
            <OpeningDescription>
              No formal qualifications are required — just a passion for gardens and a willingness to get stuck in
              outdoors. Full training and guidance will be provided by our gardens team.
            </OpeningDescription>
            <ListHeading>What You'll Be Doing</ListHeading>
            <BulletList>
              <li>Weeding, planting, pruning and deadheading across the gardens</li>
              <li>Helping maintain lawns, borders, paths and formal garden areas</li>
              <li>Supporting seasonal tasks such as mulching, leaf clearance and bed preparation</li>
              <li>Assisting the gardens team with day-to-day horticultural work</li>
              <li>Helping keep tools, sheds and working areas tidy and well organised</li>
            </BulletList>
            <ListHeading>What We're Looking For</ListHeading>
            <BulletList>
              <li>A genuine interest in gardens, plants or outdoor work</li>
              <li>Happy to get stuck in with practical, physical gardening tasks</li>
              <li>Comfortable spending time outdoors in all weathers</li>
              <li>Reliable and happy to commit to agreed volunteering days</li>
            </BulletList>
            <ListHeading>Volunteer Benefits</ListHeading>
            <VolunteerBenefits>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faIdCard} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>Free Friends of Belvoir membership for you and +1</VolunteerBenefitText>
              </VolunteerBenefitItem>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faGlassCheers} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>Staff social events included</VolunteerBenefitText>
              </VolunteerBenefitItem>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faCar} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>Travel expenses paid</VolunteerBenefitText>
              </VolunteerBenefitItem>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faPercent} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>20% off family and friend event tickets</VolunteerBenefitText>
              </VolunteerBenefitItem>
            </VolunteerBenefits>
            <ButtonRow>
              <ApplyLink to="/about/careers/volunteer-garden-guide">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/volunteer-garden-guide">
                View Full Spec →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>

          {/* ── Volunteer Castle Guide ── */}
          <OpeningCard>
            <OpeningTitle>
              Volunteer Castle Guide
              <NewBadge>Volunteer</NewBadge>
            </OpeningTitle>
            <OpeningMeta>
              Belvoir Castle, Leicestershire &nbsp;·&nbsp; Volunteer &nbsp;·&nbsp; Flexible days
            </OpeningMeta>
            <OpeningDescription>
              Become part of the story of Belvoir Castle. We're looking for enthusiastic Volunteer Castle Guides to
              welcome visitors and bring the castle's rich history to life through engaging tours and conversation
              across our State Rooms and grounds.
            </OpeningDescription>
            <OpeningDescription>
              This is a wonderful opportunity for history lovers to spend time in a spectacular setting, meet people
              from all over the world, and be part of a friendly, welcoming volunteer team. Full training on the
              castle's history and collections will be provided.
            </OpeningDescription>
            <ListHeading>What You'll Be Doing</ListHeading>
            <BulletList>
              <li>Guiding visitors around the State Rooms and sharing the castle's history</li>
              <li>Bringing to life stories of the Dukes and Duchesses of Rutland and the Manners family</li>
              <li>Answering visitor questions and helping create a memorable castle experience</li>
              <li>Keeping a friendly, watchful eye over the collections while visitors explore</li>
              <li>Working alongside our House team on open days</li>
            </BulletList>
            <ListHeading>What We're Looking For</ListHeading>
            <BulletList>
              <li>A passion for history, heritage and storytelling</li>
              <li>Confident, friendly and comfortable speaking with groups of visitors</li>
              <li>Reliable and happy to commit to agreed volunteering days</li>
              <li>Previous guiding or public speaking experience is helpful but not essential</li>
            </BulletList>
            <ListHeading>Volunteer Benefits</ListHeading>
            <VolunteerBenefits>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faIdCard} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>Free Friends of Belvoir membership for you and +1</VolunteerBenefitText>
              </VolunteerBenefitItem>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faGlassCheers} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>Staff social events included</VolunteerBenefitText>
              </VolunteerBenefitItem>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faCar} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>Travel expenses paid</VolunteerBenefitText>
              </VolunteerBenefitItem>
              <VolunteerBenefitItem>
                <VolunteerBenefitIcon>
                  <FontAwesomeIcon icon={faPercent} />
                </VolunteerBenefitIcon>
                <VolunteerBenefitText>20% off family and friend event tickets</VolunteerBenefitText>
              </VolunteerBenefitItem>
            </VolunteerBenefits>
            <ButtonRow>
              <ApplyLink to="/about/careers/volunteer-castle-guide">
                Apply Now
              </ApplyLink>
              <ViewFullSpec to="/about/careers/volunteer-castle-guide">
                View Full Spec →
              </ViewFullSpec>
            </ButtonRow>
          </OpeningCard>
        </CurrentOpenings>
        
        <ContactSection>
          <ContactTitle>Join Our Team</ContactTitle>
          <ContactText>
            If you don't see a role that matches your skills but believe you could contribute 
            to our team, we'd love to hear from you. Send your CV and a cover letter to our 
            HR department.
          </ContactText>
          <ApplyButton 
            onClick={() => {
              try {
                window.open('mailto:info@belvoircastle.com', '_blank');
              } catch (e) {
                alert('Please email us at info@belvoircastle.com');
              }
            }}
          >
            Contact Us
          </ApplyButton>
        </ContactSection>
      </ContentContainer>
      
      <Footer />
    </CareersContainer>
  );
};

export default Careers; 